import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

import PsContext from "./index";
import { getLs, setLs, upperCase } from "../utils";
import { listSettingsFields } from "../models/fieldSettings";
import { Spinner } from "react-bootstrap";
import { isSessionExpired, SESSION_TIMEOUT_MS } from "./session";

import jwt from "jwt-simple";

const PsContextProvider = (props) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const breakpoint = 620;

  const token = "21R91541sD5411H0G5";

  const isMobileView = () => (windowWidth < breakpoint ? true : false);

  const checkUserLogged = () => {
    return getLs("billnote_logged") || "no";
  };

  const checkUserMobilePassword = () => {
    return getLs("billnote_mob_pass") || "";
  };

  const checkMobileUserId = () => {
    return getLs("billnote_mob_uuid") || "";
  };

  const getAdminUser = () => {
    return getLs("billnote_user_data") &&
      getLs("billnote_user_data") !== "undefined"
      ? JSON.parse(getLs("billnote_user_data"))
      : [];
  };

  const getAdminCompany = () => {
    return getLs("billnote_company_data") &&
      getLs("billnote_company_data") !== "undefined"
      ? JSON.parse(getLs("billnote_company_data"))
      : [];
  };

  const getAdminApi = () => {
    return getLs("billnote_api") || "";
  };

  const getSettings = () => {
    try {
      let set = getLs("adm_settings");
      if (set && set !== undefined) {
        set = set.trim();
        if (!set.includes("{")) {
          let decoded = jwt.decode(set, token);
          setSettings(decoded);
          return decoded;
        }
        return [];
      } else {
        setSettings([]);
        return [];
      }
    } catch (er) {
      console.log(er);
      return [];
    }
  };

  const getAcyear = () => {
    return getLs("billnt_acyear_cur") &&
      getLs("billnt_acyear_cur") !== "undefined"
      ? JSON.parse(getLs("billnt_acyear_cur"))
      : [];
  };

  const getPermissions = () => {
    return getLs("billnt_user_permissions") &&
      getLs("billnt_user_permissions") !== "undefined"
      ? JSON.parse(getLs("billnt_user_permissions"))
      : [];
  };

  const getCashbook = () => {
    return getLs("billnt_user_cashbook") &&
      getLs("billnt_user_cashbook") !== "undefined"
      ? JSON.parse(getLs("billnt_user_cashbook"))
      : [];
  };

  const [state, setState] = useState([]);

  const [logged, setLogged] = useState(checkUserLogged());

  const [mobilePass, setMobilePass] = useState(checkUserMobilePassword());
  const [mobileUserId, setMobileUserId] = useState(checkMobileUserId());

  const [user, setUser] = useState(getAdminUser());
  const [api, setApi] = useState(getAdminApi());

  const [backgroundProcess, setBackgroundProcess] = useState(false);

  const [settings, setSettings] = useState(getSettings());
  const [acyear, setSAcyear] = useState(getAcyear());

  const [accessPermissions, setAccessPermissions] = useState(getPermissions());
  const [cashbook, setCashbook] = useState(getCashbook());

  const [selectedAdmissionYear, setadmissionYear] = useState("2022");

  const [loader, setLoader] = useState(false);

  const updateLastActivity = () => {
    setLs("billnote_logged", "yes");
    setLs("billnote_last_activity", Date.now().toString());
  };

  const markUserActivity = useCallback(() => {
    if (getLs("billnote_logged") === "yes") {
      updateLastActivity();
    }
  }, []);

  const logout = useCallback(() => {
    setUser([]);
    setSAcyear([]);
    setApi(null);
    if (axios.defaults.headers.common["Api-Token"]) {
      delete axios.defaults.headers.common["Api-Token"];
    }
    setAccessPermissions([]);
    setCashbook([]);
    setLogged("no");
    setLs("billnote_logged", "no");
    setLs("billnote_last_activity", "");
    setLs("billnote_api", "");
    setLs("billnote_user_data", "");
    setLs("billnt_user_permissions", "");
    setLs("billnt_user_cashbook", "");

    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (er) {
      console.error("Error clearing session storage", er);
    }
  }, []);

  const saveLogin = (user, api, permissions = [], cb = [], mobPass = "") => {
    setLs("billnote_user_data", JSON.stringify(user));
    setUser(user);

    setLs("billnote_api", api);
    setApi(api);

    setLs("billnt_user_permissions", JSON.stringify(permissions));
    setAccessPermissions(permissions);

    setLs("billnt_user_cashbook", JSON.stringify(cb));
    setCashbook(cb);

    setLs("billnote_logged", "yes");
    setLs("billnote_last_activity", Date.now().toString());
    setLogged("yes");
  };

  const isSessionValid = () => {
    const isLogged = getLs("billnote_logged") === "yes";
    const currentApi = getLs("billnote_api");
    const userData = getLs("billnote_user_data");
    const hasUser =
      userData && userData !== "false" && userData !== "undefined";
    const lastActivity = getLs("billnote_last_activity");

    if (!isLogged || !currentApi || !hasUser) {
      return false;
    }

    if (!lastActivity) {
      updateLastActivity();
      return true;
    }

    return !isSessionExpired(lastActivity, Date.now());
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);

    const handleStorageChange = (e) => {
      if (
        (e.key === "billnote_logged" && e.newValue && e.newValue !== "yes") ||
        (e.key === "billnote_api" && e.oldValue && !e.newValue)
      ) {
        logout();
      }
    };

    const updateActivityOnAnyAction = () => {
      markUserActivity();
    };

    const events = [
      "click",
      "keydown",
      "keyup",
      "mousemove",
      "mousedown",
      "mouseup",
      "touchstart",
      "touchmove",
      "scroll",
      "wheel",
      "pointerdown",
      "pointermove",
      "input",
      "change",
      "paste",
      "cut",
      "beforeinput",
      "compositionstart",
      "compositionend",
      "focusin",
      "focus",
    ];

    const eventOptions = { passive: true, capture: true };

    events.forEach((eventName) => {
      window.addEventListener(eventName, updateActivityOnAnyAction, eventOptions);
      document.addEventListener(eventName, updateActivityOnAnyAction, eventOptions);
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        markUserActivity();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const inactivityTimer = setInterval(() => {
      const isLogged = getLs("billnote_logged") === "yes";
      const lastActivity = getLs("billnote_last_activity");
      if (
        isLogged &&
        lastActivity &&
        isSessionExpired(lastActivity, Date.now())
      ) {
        logout();
      }
    }, 60000);

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("storage", handleStorageChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      events.forEach((eventName) => {
        window.removeEventListener(eventName, updateActivityOnAnyAction, eventOptions);
        document.removeEventListener(eventName, updateActivityOnAnyAction, eventOptions);
      });
      clearInterval(inactivityTimer);
    };
  }, [logout, markUserActivity]);

  const decode = (v) => {
    try {
      return jwt.decode(v, token);
    } catch (er) {
      return [];
    }
  };

  const getDecodeApi = () => {
    let decoded = jwt.decode(api, token);
    return decoded;
  };

  const setEncodeApi = (decoded) => {
    let encoded = jwt.encode(decoded, token);
    axios.defaults.headers.common["Api-Token"] = encoded;
    setLs("billnote_api", encoded);
    setApi(api);
  };

  const updateUser = (us) => {
    if (us.academic_department) {
      let decoded = getDecodeApi();
      decoded.academic_department = us.academic_department;
      setEncodeApi(decoded);
    }
    setLs("billnote_user_data", JSON.stringify(us));
    setUser(us);
    forceUpdate();
  };

  const updateCashbook = (cb) => {
    let decoded = getDecodeApi();
    decoded.allowd_cash_books = cb.id;
    setEncodeApi(decoded);
    setLs("billnt_user_cashbook", JSON.stringify(cb));
    setCashbook(cb);
    forceUpdate();
  };

  const updateLogged = () => {
    setLs("billnote_logged", "yes");
    setLogged("yes");
  };

  const loadSettings = () => {
    //setLoader(true);
    try {
      listSettingsFields("1").then((res) => {
        let decoded = jwt.decode(res, token);
        setLs("billnt_settings", JSON.stringify(decoded));
        setSettings(decoded);
        //setLoader(false);
      });
    } catch (er) {
      setLoader(false);
    }
  };

  const settingValue = (key) => {
    let m = settings && settings.find((item) => item.field_name == key);
    return m && m.field_name ? m.field_value : false;
  };

  const updateAcyear = (v) => {
    setSAcyear(v);
    setLs("billnt_acyear_cur", JSON.stringify(v));
  };

  const updateMobileUserId = (v) => {
    setMobileUserId(v);
    setLs("billnote_mob_uuid", v);
  };

  const updateMobilePassword = (v) => {
    setLs("billnote_mob_pass", v);
    setMobilePass(v);
  };

  const setStorage = (storageName, storageValue) => {
    setLs(storageName, storageValue);
  };

  const getStorage = (storageName) => {
    return getLs(storageName); /* && getLs(storageName) !== "undefined"
      ? getLs(storageName)
      : null;*/
  };

  const allowedAccess = (module, action) => {
    let permissions = accessPermissions;
    try {
      if (upperCase(user.role) == "DEV") return true;

      /* if (props.roleGroup && props.roleGroup.length > 0) {
        let allowedRoleGroups = findCommonElement(
          permissions.map((item) => item.role_group),
          props.roleGroup
        );
        return allowedRoleGroups;
      }*/

      if (!module || !action) return false;

      let moduleExist = permissions.filter(
        (item) => upperCase(item.module) == upperCase(module),
      );

      if (moduleExist.length < 1) return false;

      let allowed = moduleExist.find(
        (item) => upperCase(item.action_name) == upperCase(action),
      );

      return allowed ? true : false;
    } catch (er) {
      return false;
    }
  };

  const forceUpdate = useCallback(() => setState({}), []);

  return (
    <PsContext.Provider
      value={{
        state: state,
        logged: logged,
        isSessionValid: isSessionValid,
        updateLastActivity: updateLastActivity,
        markUserActivity: markUserActivity,
        user: user,
        acyear: acyear,
        api: api,
        saveLogin: saveLogin,
        updateUser: updateUser,
        updateLogged: updateLogged,
        setBackgroundProcess: setBackgroundProcess,
        backgroundProcess: backgroundProcess,
        logout: logout,
        loadSettings: loadSettings,
        settingValue: settingValue,
        selectedAdmissionYear: selectedAdmissionYear,
        updateAcyear: updateAcyear,
        accessPermissions: accessPermissions,
        allowedAccess: allowedAccess,
        cashbook: cashbook,
        updateCashbook: updateCashbook,
        isMobileView: isMobileView,
        mobilePass: mobilePass,
        mobileUserId: mobileUserId,
        updateMobileUserId: updateMobileUserId,
        updateMobilePassword: updateMobilePassword,
        setLoader: setLoader,
        setStorage: setStorage,
        getStorage: getStorage,
        decode: decode,
        getDecodeApi: getDecodeApi,
      }}
    >
      {loader && (
        <div className="ps__loader">
          <div className="ps__loader__body">
            <div className="ps__loader__content">
              <Spinner animation="grow" size="lg" />
              <br />
              Please Wait
            </div>
          </div>
        </div>
      )}

      {props.children}
    </PsContext.Provider>
  );
};

export default PsContextProvider;
